import { cn } from '@/shared/utils/cn'
import { Icon } from '@/shared/ui/Icon'
import { APP_ENV } from '@/shared/config/env'
import type { ConversationSourceDTO, DocumentKind } from '@/shared/api'

interface Props {
  source: ConversationSourceDTO
}

function scoreBucket(score: number | null): 'high' | 'mid' | 'low' {
  if (score === null) return 'low'
  if (score >= 0.85) return 'high'
  if (score >= 0.75) return 'mid'
  return 'low'
}

function formatLabel(source: ConversationSourceDTO): string {
  const chunk =
    source.chunk_index !== null
      ? `chunk ${String(source.chunk_index).padStart(2, '0')}`
      : `#${source.rank}`
  const meta = source.metadata_json ?? {}
  const page = typeof meta.page === 'number' ? meta.page : null
  return page !== null ? `${chunk} · pág. ${page}` : chunk
}

function filename(source: ConversationSourceDTO): string {
  const meta = source.metadata_json ?? {}
  if (typeof meta.filename === 'string') return meta.filename
  if (typeof meta.title === 'string') return meta.title
  return source.document_id ?? `Fonte #${source.rank}`
}

function documentTag(source: ConversationSourceDTO): string | null {
  if (!source.document_id) return null
  // Avoid repeating the same value when filename has nothing better to offer.
  const meta = source.metadata_json ?? {}
  const filenameMeta = typeof meta.filename === 'string' ? meta.filename : null
  if (filenameMeta && filenameMeta === source.document_id) return null
  return source.document_id
}

function readDocumentKind(
  source: ConversationSourceDTO,
): DocumentKind | null {
  const meta = source.metadata_json ?? {}
  const raw = meta.document_kind
  if (raw === 'case_document' || raw === 'reference_document') return raw
  return null
}

/**
 * Step 8.1 — assemble the small "Referência" subtitle that lists whichever
 * reference metadata was indexed with the chunk (type/domain/year/source).
 * Returns ``null`` when the source carries no extra reference metadata so
 * the caller can omit the line entirely.
 */
function referenceSubtitle(source: ConversationSourceDTO): string | null {
  const meta = source.metadata_json ?? {}
  const parts: string[] = []
  if (typeof meta.reference_type === 'string') parts.push(meta.reference_type)
  if (typeof meta.reference_domain === 'string') parts.push(meta.reference_domain)
  if (typeof meta.reference_year === 'number') parts.push(String(meta.reference_year))
  else if (typeof meta.reference_year === 'string') parts.push(meta.reference_year)
  if (typeof meta.reference_source === 'string') parts.push(meta.reference_source)
  return parts.length > 0 ? parts.join(' · ') : null
}

export function SourceCard({ source }: Props) {
  const bucket = scoreBucket(source.score)
  const tag = documentTag(source)
  const kind = readDocumentKind(source)
  const refSubtitle =
    kind === 'reference_document' ? referenceSubtitle(source) : null
  return (
    <button
      type="button"
      className="source"
      data-testid="source-card"
      data-document-kind={kind ?? ''}
      title={source.content_preview ?? filename(source)}
    >
      <div className="source-icon">
        <Icon name={kind === 'reference_document' ? 'book' : 'doc'} />
      </div>
      <div className="source-meta">
        <span className="source-name">{filename(source)}</span>
        <span className="source-sub">
          {kind && (
            <span
              className={cn(
                'mr-1.5 inline-flex items-center rounded-md border px-1.5 py-px text-[10px] font-medium uppercase tracking-[0.08em]',
                kind === 'reference_document'
                  ? 'border-violet/40 bg-violet/[0.12] text-violet'
                  : 'border-white/10 bg-white/[0.04] text-text-mute',
              )}
            >
              {kind === 'reference_document' ? 'Referência' : 'Caso'}
            </span>
          )}
          {formatLabel(source)}
          {tag && (
            <>
              {' · '}
              <span className="font-mono text-[10.5px] opacity-80">{tag}</span>
            </>
          )}
        </span>
        {refSubtitle && (
          <span className="source-sub mt-0.5 text-[10.5px] text-text-dim">
            {refSubtitle}
          </span>
        )}
      </div>
      {/* Step 13.7 — pontuação bruta de similaridade só faz sentido em dev:
          é ruído (e enganosa no fallback lexical, onde score=0) para o usuário
          final. Oculta em builds de produção; visível em `vite dev`. */}
      {APP_ENV.isDev && (
        <span
          className={cn('source-score', {
            mid: bucket === 'mid',
            low: bucket === 'low',
          })}
        >
          {source.score !== null ? source.score.toFixed(3) : '—'}
        </span>
      )}
    </button>
  )
}
